import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, FileText, CheckCircle2, ShieldCheck, PenLine, Info, RotateCcw, Eye, EyeOff } from 'lucide-react';
export interface SourceOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  sourceType: 'email' | 'document';
  sourceTitle: string;
  sourceDate: string;
  extractedValue: string;
  context?: string;
}
export function SourceOverlay({
  isOpen,
  onClose,
  sourceType,
  sourceTitle,
  sourceDate,
  extractedValue,
  context
}: SourceOverlayProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [overrideValue, setOverrideValue] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState<string | null>(null);
  const [editDraftValue, setEditDraftValue] = useState(extractedValue);
  const [editDraftReason, setEditDraftReason] = useState('');
  const [showSource, setShowSource] = useState(false);
  // Reset state when modal opens with new value
  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setOverrideValue(null);
      setOverrideReason(null);
      setEditDraftValue(extractedValue);
      setEditDraftReason('');
      setShowSource(false);
    }
  }, [isOpen, extractedValue]);
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const isEmail = sourceType === 'email';
  const Icon = isEmail ? Mail : FileText;
  const themeColor = isEmail ? 'teal' : 'blue';
  const displayValue = overrideValue !== null ? overrideValue : extractedValue;
  const isOverridden = overrideValue !== null;
  const handleSaveOverride = () => {
    setOverrideValue(editDraftValue);
    setOverrideReason(editDraftReason);
    setIsEditing(false);
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditDraftValue(displayValue);
    setEditDraftReason(overrideReason || '');
  };
  const handleUndoOverride = () => {
    setOverrideValue(null);
    setOverrideReason(null);
    setEditDraftValue(extractedValue);
    setEditDraftReason('');
  };
  const overlayContent = <AnimatePresence data-id="element-726">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" data-id="element-727">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-gray-900/60" data-id="element-728" />

          {/* Modal Container */}
          <motion.div layout initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`relative w-full bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden ${showSource ? 'max-w-5xl h-[85vh]' : 'max-w-lg'}`} data-id="element-729">
            {/* Header */}
            <motion.div layout="position" className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0" data-id="element-730">
              <div className="flex items-center gap-3" data-id="element-731">
                <div className={`p-2 rounded-lg ${isEmail ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-732">
                  <Icon className="w-5 h-5" data-id="element-733" />
                </div>
                <div data-id="element-734">
                  <h2 className="text-base font-bold text-gray-900 leading-tight" data-id="element-735">
                    {sourceTitle}
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-736">{sourceDate}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" data-id="element-737">
                <X className="w-5 h-5" data-id="element-738" />
              </button>
            </motion.div>

            {/* Body */}
            <div className={`flex flex-col ${showSource ? 'flex-1 overflow-hidden' : ''}`} data-id="element-739">
              {/* Value Section (Always visible) */}
              <motion.div layout="position" className="p-6 flex flex-col shrink-0" data-id="element-740">
                <div className="flex items-center justify-between mb-3" data-id="element-741">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-742">
                    {context || 'Extracted Value'}
                  </h3>
                  {isOverridden && <div className="group relative flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2 py-1 rounded border border-indigo-100 cursor-help" data-id="element-743">
                      <Info className="w-3 h-3" data-id="element-744" />
                      Overridden
                      <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl normal-case tracking-normal font-normal" data-id="element-745">
                        <p className="font-semibold mb-1 text-gray-300" data-id="element-746">
                          Original AI Extraction:
                        </p>
                        <p className="leading-relaxed" data-id="element-747">{extractedValue}</p>
                        {overrideReason && <>
                            <p className="font-semibold mt-2 mb-1 text-gray-300" data-id="element-748">
                              Override Reason:
                            </p>
                            <p className="leading-relaxed" data-id="element-749">
                              {overrideReason}
                            </p>
                          </>}
                      </div>
                    </div>}
                </div>

                {!isEditing ? <div className={`border rounded-lg p-4 mb-5 ${isOverridden ? 'bg-indigo-50/30 border-indigo-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-750">
                    <p className="text-base font-medium text-gray-900 leading-snug" data-id="element-751">
                      {displayValue}
                    </p>
                  </div> : <div className="space-y-4 mb-5" data-id="element-752">
                    <div data-id="element-753">
                      <textarea value={editDraftValue} onChange={e => setEditDraftValue(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm text-gray-900 bg-white resize-none h-24" placeholder="Enter override value..." data-id="element-754" />
                    </div>
                    <div data-id="element-755">
                      <input type="text" value={editDraftReason} onChange={e => setEditDraftReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm text-gray-900 bg-white" placeholder="Reason for override (optional)..." data-id="element-756" />
                    </div>
                    <div className="flex gap-3" data-id="element-757">
                      <button onClick={handleSaveOverride} className="flex-1 bg-gray-900 text-white font-medium py-2 rounded-md hover:bg-gray-800 transition-colors text-sm" data-id="element-758">
                        Save Override
                      </button>
                      <button onClick={handleCancelEdit} className="flex-1 bg-white text-gray-700 border border-gray-300 font-medium py-2 rounded-md hover:bg-gray-50 transition-colors text-sm" data-id="element-759">
                        Cancel
                      </button>
                    </div>
                  </div>}

                {!isEditing && <div className="flex items-center justify-between" data-id="element-760">
                    <div className="flex items-center gap-3" data-id="element-761">
                      <button onClick={() => setIsEditing(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-colors px-3 py-1.5 rounded-md" data-id="element-762">
                        <PenLine className="w-4 h-4" data-id="element-763" />
                        Override
                      </button>
                      <button onClick={() => setShowSource(!showSource)} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors px-3 py-1.5 rounded-md" data-id="element-764">
                        {showSource ? <>
                            <EyeOff className="w-4 h-4" data-id="element-765" />
                            Hide Source
                          </> : <>
                            <Eye className="w-4 h-4" data-id="element-766" />
                            View Source
                          </>}
                      </button>
                    </div>
                    <div className="flex flex-col items-end gap-1" data-id="element-767">
                      {isOverridden && <button onClick={handleUndoOverride} className="text-xs text-rose-500 hover:text-rose-700 font-medium flex items-center gap-1" data-id="element-768">
                          <RotateCcw className="w-3 h-3" data-id="element-769" /> Undo Override
                        </button>}
                      <span className="text-[10px] text-gray-400" data-id="element-770">
                        {isOverridden ? 'Overridden by User on ' : 'Verified by AltConvey AI on '}
                        {new Date().toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                      </span>
                    </div>
                  </div>}
              </motion.div>

              {/* Source Section (Expanded only) */}
              <AnimatePresence data-id="element-771">
                {showSource && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="flex-1 flex flex-col border-t border-gray-200 overflow-hidden" data-id="element-772">
                    {/* Confidence Badge Bar */}
                    <div className="bg-gray-50 border-b border-gray-200 px-6 py-2.5 flex items-center gap-2 shrink-0" data-id="element-773">
                      {isOverridden ? <div className="flex items-center gap-1.5 text-indigo-600" data-id="element-774">
                          <PenLine className="w-4 h-4" data-id="element-775" />
                          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-776">
                            User Override
                          </span>
                        </div> : <div className="flex items-center gap-1.5 text-emerald-600" data-id="element-777">
                          <ShieldCheck className="w-4 h-4" data-id="element-778" />
                          <span className="text-xs font-bold uppercase tracking-wider" data-id="element-779">
                            High Confidence Extraction
                          </span>
                        </div>}
                    </div>

                    {/* Source Document Preview */}
                    <div className="flex-1 overflow-y-auto bg-gray-100 p-6" data-id="element-780">
                      {isEmail ? <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden max-w-3xl mx-auto" data-id="element-781">
                          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 space-y-2" data-id="element-782">
                            <div className="flex justify-between text-sm" data-id="element-783">
                              <span className="text-gray-500 w-16" data-id="element-784">From:</span>
                              <span className="flex-1 font-medium text-gray-900" data-id="element-785">
                                legal@gpcounsel.com
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-786">
                              <span className="text-gray-500 w-16" data-id="element-787">To:</span>
                              <span className="flex-1 text-gray-700" data-id="element-788">
                                deal-team@altconvey.com
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-789">
                              <span className="text-gray-500 w-16" data-id="element-790">Subject:</span>
                              <span className="flex-1 font-medium text-gray-900" data-id="element-791">
                                {sourceTitle}
                              </span>
                            </div>
                          </div>
                          <div className="p-6 text-sm text-gray-800 leading-relaxed space-y-4 font-serif" data-id="element-792">
                            <p data-id="element-793">Hi Team,</p>
                            <p data-id="element-794">
                              Please find attached the latest responses to the DDQ. We
                              have reviewed the partial responses and are currently
                              waiting on the tax section.
                            </p>
                            <p data-id="element-795">
                              Regarding the next steps,{' '}
                              <span className="bg-teal-100 text-teal-900 px-1 py-0.5 rounded font-medium border border-teal-200" data-id="element-796">
                                {displayValue}
                              </span>
                              . We expect to have this completed shortly.
                            </p>
                            <p data-id="element-797">Let me know if you need anything else.</p>
                            <p className="text-gray-500 pt-4" data-id="element-798">
                              Best regards,
                              <br data-id="element-799" />
                              GP Counsel Team
                            </p>
                          </div>
                        </div> : <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 min-h-[800px] mx-auto max-w-3xl font-serif" data-id="element-800">
                          <div className="text-center mb-10 border-b border-gray-300 pb-6" data-id="element-801">
                            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-802">
                              TRANSFER AGREEMENT
                            </h1>
                            <p className="text-gray-500 text-sm uppercase tracking-widest" data-id="element-803">
                              Execution Version
                            </p>
                          </div>
                          <div className="space-y-6 text-sm text-gray-800 leading-loose text-justify" data-id="element-804">
                            <p data-id="element-805">
                              THIS TRANSFER AGREEMENT (this "Agreement") is made and
                              entered into as of the date first written above by and
                              among the parties listed on Schedule A hereto.
                            </p>
                            <p data-id="element-806">
                              WHEREAS, the Seller desires to transfer, and the Buyer
                              desires to acquire, the Transferred Interest subject to
                              the terms and conditions set forth herein.
                            </p>
                            <div className="pl-6 border-l-4 border-blue-200 bg-blue-50/30 py-2 pr-4 my-6" data-id="element-807">
                              <p className="font-medium text-gray-900 mb-2" data-id="element-808">
                                Section 2.1. Conditions Precedent.
                              </p>
                              <p data-id="element-809">
                                The obligations of the parties to consummate the
                                transactions contemplated hereby are subject to the
                                satisfaction or waiver of the following conditions:
                                <br data-id="element-810" />
                                (a) The GP shall have provided written consent;
                                <br data-id="element-811" />
                                (b){' '}
                                <span className="bg-blue-100 text-blue-900 px-1 py-0.5 rounded font-medium border border-blue-200" data-id="element-812">
                                  {displayValue}
                                </span>
                              </p>
                            </div>
                            <p data-id="element-813">
                              IN WITNESS WHEREOF, the parties have executed this
                              Agreement as of the date first written above.
                            </p>
                          </div>
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
  // Render in portal to avoid overflow issues
  return createPortal(overlayContent, document.body);
}